/*
 * ============================================================================
 * Shopify Standard Product Taxonomy - lookup for the two attributes the MC
 * product page's colour picker depends on: `color` and `pattern`.
 * ============================================================================
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * `product.metafields.shopify.color-pattern` entries carry a
 * `pattern_taxonomy_reference`, but the storefront drop only ever exposes that
 * as a bare GID string:
 *
 *     gid://shopify/TaxonomyValue/2874
 *
 * `.value.name` and `.value.handle` are empty in Liquid, and `| json` is not
 * allowed on a taxonomy reference at all. So the name ("Solid") is not
 * reachable at render time. This table closes that gap client-side.
 *
 * WHERE THE DATA COMES FROM
 * -------------------------
 * Shopify publishes the whole taxonomy as open data:
 *
 *     https://github.com/Shopify/product-taxonomy
 *     dist/en/attributes.json   -> every attribute, with its values as
 *                                  { id, name, handle }
 *
 * The two attributes below were extracted verbatim from that file
 * (taxonomy version 2026-11-unstable). IDs are stable, so this table does not
 * need to be regenerated often. To refresh it, re-read attributes.json and
 * rebuild the two maps.
 *
 * USAGE
 * -----
 *   TaxonomyColourPattern.name('gid://shopify/TaxonomyValue/2874')  // "Solid"
 *   TaxonomyColourPattern.isSolid(gid)                              // true
 *   TaxonomyColourPattern.SOLID_GID                                 // the Solid GID
 */
(function () {
  'use strict';

  /*
   * The single most important constant for the picker.
   *
   * `pattern_taxonomy_reference` is "Solid" for every flat colour, and some
   * other Pattern value (e.g. "Abstract") for artwork/patterned colours. The
   * picker branches on exactly this:
   *
   *   pattern === SOLID_GID  (or no reference at all) -> hex swatch from `color`
   *   pattern !== SOLID_GID                           -> variant image button
   */
  var SOLID_GID = 'gid://shopify/TaxonomyValue/2874';

  // TaxonomyAttribute/1 - "Pattern" - 51 values
  var PATTERN = {
    'gid://shopify/TaxonomyValue/24477': 'Abstract',
    'gid://shopify/TaxonomyValue/24478': 'Animal',
    'gid://shopify/TaxonomyValue/24479': 'Art',
    'gid://shopify/TaxonomyValue/24480': 'Bead & reel',
    'gid://shopify/TaxonomyValue/2283': 'Birds',
    'gid://shopify/TaxonomyValue/24481': 'Brick',
    'gid://shopify/TaxonomyValue/24482': "Bull's eye",
    'gid://shopify/TaxonomyValue/2866': 'Camouflage',
    'gid://shopify/TaxonomyValue/2867': 'Characters',
    'gid://shopify/TaxonomyValue/2868': 'Checkered',
    'gid://shopify/TaxonomyValue/24483': 'Chevron',
    'gid://shopify/TaxonomyValue/24484': 'Chinoiserie',
    'gid://shopify/TaxonomyValue/2869': 'Christmas',
    'gid://shopify/TaxonomyValue/24485': 'Collage',
    'gid://shopify/TaxonomyValue/24486': 'Coral',
    'gid://shopify/TaxonomyValue/24487': 'Damask',
    'gid://shopify/TaxonomyValue/24488': 'Diagonal',
    'gid://shopify/TaxonomyValue/24489': 'Diamond',
    'gid://shopify/TaxonomyValue/24490': "Dog's tooth",
    'gid://shopify/TaxonomyValue/2870': 'Dots',
    'gid://shopify/TaxonomyValue/24491': 'Egg & dart',
    'gid://shopify/TaxonomyValue/24492': 'Ethnic',
    'gid://shopify/TaxonomyValue/24493': 'Everlasting knot',
    'gid://shopify/TaxonomyValue/2871': 'Floral',
    'gid://shopify/TaxonomyValue/24494': 'Fret',
    'gid://shopify/TaxonomyValue/1868': 'Geometric',
    'gid://shopify/TaxonomyValue/24495': 'Guilloche',
    'gid://shopify/TaxonomyValue/2375': 'Hearts',
    'gid://shopify/TaxonomyValue/24496': 'Illusion',
    'gid://shopify/TaxonomyValue/28170': 'Lace',
    'gid://shopify/TaxonomyValue/2872': 'Leaves',
    'gid://shopify/TaxonomyValue/24497': 'Logo',
    'gid://shopify/TaxonomyValue/24498': 'Mosaic',
    'gid://shopify/TaxonomyValue/24499': 'Ogee',
    'gid://shopify/TaxonomyValue/24500': 'Organic',
    'gid://shopify/TaxonomyValue/2873': 'Paisley',
    'gid://shopify/TaxonomyValue/24501': 'Plaid',
    'gid://shopify/TaxonomyValue/24502': 'Rainbow',
    'gid://shopify/TaxonomyValue/24503': 'Random',
    'gid://shopify/TaxonomyValue/24504': 'Scale',
    'gid://shopify/TaxonomyValue/24505': 'Scroll',
    'gid://shopify/TaxonomyValue/2874': 'Solid',
    'gid://shopify/TaxonomyValue/2376': 'Stars',
    'gid://shopify/TaxonomyValue/2875': 'Striped',
    'gid://shopify/TaxonomyValue/24506': 'Swirl',
    'gid://shopify/TaxonomyValue/1782': 'Text',
    'gid://shopify/TaxonomyValue/24507': 'Texture',
    'gid://shopify/TaxonomyValue/2876': 'Tie-dye',
    'gid://shopify/TaxonomyValue/24508': 'Trellis',
    'gid://shopify/TaxonomyValue/1796': 'Vehicle',
    'gid://shopify/TaxonomyValue/24509': 'Other'
  };

  /*
   * TaxonomyAttribute/1 - "Color" - 19 values (deliberately a SHORT list).
   *
   * Note what is NOT here: "Light Blue", "Navy Blue", "Charcoal", "Ivory".
   * Shopify's Color attribute only covers these 19 base colours, so a store
   * colour named "Light Blue" cannot be a Color reference - its taxonomy
   * reference will be a PATTERN reference (Solid), with the real nuance living
   * in the metaobject's own text fields instead.
   */
  var COLOR = {
    'gid://shopify/TaxonomyValue/1': 'Black',
    'gid://shopify/TaxonomyValue/2': 'Blue',
    'gid://shopify/TaxonomyValue/3': 'White',
    'gid://shopify/TaxonomyValue/4': 'Gold',
    'gid://shopify/TaxonomyValue/5': 'Silver',
    'gid://shopify/TaxonomyValue/6': 'Beige',
    'gid://shopify/TaxonomyValue/7': 'Brown',
    'gid://shopify/TaxonomyValue/8': 'Gray',
    'gid://shopify/TaxonomyValue/9': 'Green',
    'gid://shopify/TaxonomyValue/10': 'Orange',
    'gid://shopify/TaxonomyValue/11': 'Pink',
    'gid://shopify/TaxonomyValue/12': 'Purple',
    'gid://shopify/TaxonomyValue/13': 'Red',
    'gid://shopify/TaxonomyValue/14': 'Yellow',
    'gid://shopify/TaxonomyValue/15': 'Navy',
    'gid://shopify/TaxonomyValue/16': 'Rose gold',
    'gid://shopify/TaxonomyValue/17': 'Clear',
    'gid://shopify/TaxonomyValue/657': 'Bronze',
    'gid://shopify/TaxonomyValue/2865': 'Multicolor'
  };

  function normalise(gid) {
    return gid === null || gid === undefined ? '' : String(gid).trim();
  }

  var api = {
    SOLID_GID: SOLID_GID,
    PATTERN_ATTRIBUTE: 'gid://shopify/TaxonomyAttribute/3',
    COLOR_ATTRIBUTE: 'gid://shopify/TaxonomyAttribute/1',
    pattern: PATTERN,
    color: COLOR,

    // Resolve a GID to its taxonomy name, e.g. "Solid". '' when unknown.
    name: function (gid) {
      var key = normalise(gid);
      return PATTERN[key] || COLOR[key] || '';
    },

    // Is this GID the Pattern value "Solid"?
    isSolid: function (gid) {
      return normalise(gid) === SOLID_GID;
    },

    /*
     * The picker's decision, in one place.
     *
     * A missing reference counts as Solid: a colour with a hex but no pattern
     * set is a flat colour, which is exactly what a hex swatch is for.
     */
    isSolidOrUnset: function (gid) {
      var key = normalise(gid);
      return key === '' || key === SOLID_GID;
    },

    // "Solid" / "Abstract" / '(no reference)' - for logs and diagnostics.
    describe: function (gid) {
      var key = normalise(gid);
      if (key === '') return '(no reference)';
      return api.name(key) || ('(unknown: ' + key + ')');
    }
  };

  window.TaxonomyColourPattern = api;
})();
